import { create } from "zustand";
import { apiFetch, trackEvent } from "../api/client";

// Emergent-managed Google login: redirect out, return with #session_id=... in the URL fragment.
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

export const useAuth = create((set, get) => ({
  user: null,
  loading: true,
  modalOpen: false,
  modalMode: "signup", // 'signup' | 'login'
  profileOpen: false,
  adminOpen: false,
  error: null,

  openModal: (mode = "signup") => set({ modalOpen: true, modalMode: mode, error: null }),
  closeModal: () => set({ modalOpen: false, error: null }),
  openProfile: () => set({ profileOpen: true }),
  closeProfile: () => set({ profileOpen: false }),
  openAdmin: () => set({ adminOpen: true }),
  closeAdmin: () => set({ adminOpen: false }),

  // Called once on app load: pick up a Google redirect, else fetch existing session.
  init: async () => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      try {
        const user = await apiFetch("/api/auth/google/session", { method: "POST", body: { session_id: decodeURIComponent(m[1]) } });
        set({ user, loading: false });
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        return;
      } catch (e) { /* fall through */ }
    }
    try {
      const user = await apiFetch("/api/auth/me");
      set({ user, loading: false });
    } catch {
      set({ user: null, loading: false });
    }
  },

  register: async (payload) => {
    set({ error: null });
    try {
      const user = await apiFetch("/api/auth/register", { method: "POST", body: payload });
      set({ user, modalOpen: false });
      return true;
    } catch (e) { set({ error: e.message }); return false; }
  },

  login: async (email, password) => {
    set({ error: null });
    try {
      const user = await apiFetch("/api/auth/login", { method: "POST", body: { email, password } });
      set({ user, modalOpen: false });
      return true;
    } catch (e) { set({ error: e.message }); return false; }
  },

  loginWithGoogle: () => {
    trackEvent("auth_google_start", {});
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  },

  updateProfile: async (patch) => {
    try {
      const user = await apiFetch("/api/auth/profile", { method: "PATCH", body: patch });
      set({ user });
      return true;
    } catch (e) { set({ error: e.message }); return false; }
  },

  logout: async () => {
    try { await apiFetch("/api/auth/logout", { method: "POST" }); } catch { /* ignore */ }
    set({ user: null, profileOpen: false, adminOpen: false });
  },
}));
